/**
 * importengine domain — React Query hooks.
 *
 * The import-previews list is a straight re-export of the generated query
 * hook. `deleteImportPreview` is wrapped via `useInvalidatingMutation` so
 * it invalidates the list cache on success.
 *
 * Every other importengine endpoint is consumed imperatively (from the
 * XState wizard actors and the contact-import step handlers), so those
 * live as plain async functions in `./utils`, not as hooks.
 */

import { useQuery, type UseQueryOptions } from '@tanstack/react-query'

import type { ImportPreviewNoData } from 'features/importerv2/importer-types'

import { useInvalidatingMutation } from '../invalidation/useInvalidatingMutation'
import { invalidationRegistry } from './invalidation'
import { deleteImportPreview } from '../generated/clients/deleteImportPreview'
import { listImportPreviews } from '../generated/clients/listImportPreviews'
import { listImportPreviewsQueryKey } from '../generated/hooks/importengineController'
import type { DeleteImportPreviewPathParams } from '../generated/types/DeleteImportPreview'

// ── Query hooks ─────────────────────────────────────────────────────

export { listImportPreviewsQueryKey }

/**
 * List import previews for the current company.
 *
 * Hand-written wrapper around the generated `listImportPreviews` client:
 * the generated `ImportPreviewSerializerNoData` type omits fields the UI
 * relies on (`user`, `type`) and widens `status` to a generated enum, so
 * this casts to the canonical `ImportPreviewNoData` model. Keeps the
 * legacy `refetchOnMount: true` behavior.
 */
export function useListImportPreviews(
  options: {
    query?: Omit<
      UseQueryOptions<ImportPreviewNoData[], Error>,
      'queryKey' | 'queryFn'
    >
  } = {}
) {
  const { query = {} } = options
  return useQuery<ImportPreviewNoData[], Error>({
    queryKey: listImportPreviewsQueryKey(),
    queryFn: async ({ signal }) =>
      (await listImportPreviews({
        signal,
      })) as unknown as ImportPreviewNoData[],
    refetchOnMount: true,
    ...query,
  })
}

// ── Mutation hooks with auto-invalidation (WRITE) ───────────────────

/**
 * Delete an import preview. Invalidates the import-previews list on
 * success (see `invalidationRegistry`). Retains the legacy success toast.
 */
export function useDeleteImportPreview() {
  return useInvalidatingMutation('deleteImportPreview', invalidationRegistry, {
    mutationFn: (variables: {
      import_number: DeleteImportPreviewPathParams['import_number']
    }) =>
      deleteImportPreview(
        { import_number: variables.import_number },
        { successMessage: 'Import preview deleted successfully' }
      ),
  })
}
